/**
 * usePlanApprover — may the signed-in person close this plan, authorize it,
 * say whose plan it is and when it takes effect? (build contract §2.8)
 *
 * The answer is the database's: `can_authorize_plan_of_record` (migrations 785,
 * 843), asked through assertPlanningApprover — the same question
 * `close_plan_cycle`, `authorize_por`, `set_plan_scope` and
 * `set_plan_takes_effect` ask. Workspace admin or owner, or a role carrying
 * process.publish or process.legitimacy_advance (Priority Ratifier holds both),
 * and, when the governing cycle names an "Items / selection" decision right,
 * holding it.
 *
 * `null` while the answer is on its way: draw nothing yet, neither the control
 * nor the line that replaces it.
 */
import { useEffect, useState } from 'react';
import { assertPlanningApprover } from '@/lib/planningService';

export function usePlanApprover(planCycleId: string | null | undefined): boolean | null {
  const [allowed, setAllowed] = useState<boolean | null>(null);

  useEffect(() => {
    setAllowed(null);
    if (!planCycleId) return;
    let live = true;
    void assertPlanningApprover(planCycleId).then((denied) => {
      if (live) setAllowed(denied === null);
    });
    return () => {
      live = false;
    };
  }, [planCycleId]);

  return allowed;
}

/** The plain line drawn in place of Close plan for someone who may not close it. */
export const PLAN_CLOSE_AUTHORITY_LINE =
  'Closing this plan takes a workspace admin or owner, or someone whose role carries Publish or Advance to Legitimacy (the Priority Ratifier holds both).';
